import React from 'react';
import PropTypes from 'prop-types';
import { withSize } from 'react-sizeme';
import classnames from 'classnames';
import themeable from 'zignal-landscape/lib/themeable';

import ClusterForceGraph from './components/ClusterForceGraph';
import {compareClusterNodes} from '../../dataUtils';

const SIZE_ME_CONFIG = {
  refreshMode: 'debounce',
  refreshRate: 100
};

class ClusterGraph extends React.Component {
  constructor(props) {
    super(props);

    this.handleOnMouseOver = this.handleOnMouseOver.bind(this);
    this.handleOnMouseLeave = this.handleOnMouseLeave.bind(this);
    this.handleDelayTooltip = this.handleDelayTooltip.bind(this);
    this.handleLinkColor = this.handleLinkColor.bind(this);
    this.handleNodeCanvasObject = this.handleNodeCanvasObject.bind(this);
    this.handleOnNodeHover = this.handleOnNodeHover.bind(this);
    this.handleNodeVal = this.handleNodeVal.bind(this);
    this.setupForceGraphRef = this.setupForceGraphRef.bind(this);

    this.state = {
      tempHoverId: null,
      mouseActive: false
    };
  }

  shouldComponentUpdate(nextProps) {
    return (
      this.props.hoverMap.hoverId !== nextProps.hoverMap.hoverId
      || this.props.size.width !== nextProps.size.width
      || this.props.showSelectionOnly !== nextProps.showSelectionOnly
      || this.props.selectedViewByOption !== nextProps.selectedViewByOption
      || this.props.filteredAutomationScore !== nextProps.filteredAutomationScore
      || this.props.sizeBy !== nextProps.sizeBy
      || (
        this.props.showSelectionOnly
        && this.props.selectedTopClustersByVolume.length !== nextProps.selectedTopClustersByVolume.length
      )
      || compareClusterNodes(this.props.clusterNodes, nextProps.clusterNodes)
    );
  }

  setupForceGraphRef(el) {
    this.forceGraphRef = el;
  }

  handleOnMouseOver() {
    this.setState({mouseActive: true});
  }

  handleOnMouseLeave() {
    this.props.setGraphHover(null);
    this.setState({mouseActive: false, tempHoverId: null});
  }

  handleDelayTooltip(node) {
    const { id: hoverId } = node;
    this.setState({tempHoverId: hoverId});
    this.props.setGraphHover(null);
    window.setTimeout(() => {
      if (hoverId && hoverId === this.state.tempHoverId && this.state.mouseActive){
        this.props.setGraphHover(node);
      }
    }, 300);
  }

  handleLinkColor(link) {
    const { hoverMap, getLinkColor } = this.props;
    return getLinkColor(link, hoverMap);
  }

  handleNodeCanvasObject(node, ctx, globalScale) {
    const { drawNodeToCanvas, sizeBy, hoverMap } = this.props;
    drawNodeToCanvas(node, ctx, globalScale, sizeBy, hoverMap);
  }

  handleOnNodeHover(node) {
    const { setGraphHover, hoverMap: { hoverId } } = this.props;
    const { mouseActive } = this.state;
    if (mouseActive){
      const { id } = node || {};
      if (node && !hoverId || id !== hoverId){
        /*
         * force-graph 1.51.0 uses '.float-tooltip-kap' for its internal tooltip element
         * (was '.graph-tooltip' in 1.32.x). The element tracks mouse position within the canvas.
         */
        const el = window.$('.float-tooltip-kap');
        const topCss = el.css('top') || '0px';
        const leftCss = el.css('left') || '0px';
        const top = parseFloat(topCss.slice(0, -2));
        const left = parseFloat(leftCss.slice(0, -2));
        this.handleDelayTooltip(Object.assign({position: {top, left}}, node));
      } else {
        this.setState({ tempHoverId: null });
        setGraphHover(null);
      }
    }
  }

  handleNodeVal(node){
    const { sizeBy } = this.props;
    if(node.normalizedSizes === undefined) {
      return node.size;
    }
    return node.normalizedSizes[sizeBy];
  }

  render() {
    const {
      styles,
      size,
      clusterLinks,
      filteredAutomationScore,
      clusterNodes,
      authorNodes,
      showSelectionOnly,
      selectedTopClustersByVolume,
      tooltip,
      hoverMap,
      sizeBy
    } = this.props;

    const { hoverId } = hoverMap;
    const classes = {[styles.ClusterGraph]: true, [styles.ClusterGraphOpaque]: !!hoverId };

    return (
      <div className={classnames(classes)}>
        <div
          className={styles.ClusterGraphChild}
          onMouseOver={this.handleOnMouseOver}
          onMouseLeave={this.handleOnMouseLeave}
        >
          <ClusterForceGraph
            ref={this.setupForceGraphRef}
            width={size.width - 20}
            clusterLinks={clusterLinks}
            filteredAutomationScore={filteredAutomationScore}
            clusterNodes={clusterNodes}
            authorNodes={authorNodes}
            showSelectionOnly={showSelectionOnly}
            selectedTopClustersByVolume={selectedTopClustersByVolume}
            nodeVal={this.handleNodeVal}
            linkColor={this.handleLinkColor}
            onNodeHover={this.handleOnNodeHover}
            nodeCanvasObject={this.handleNodeCanvasObject}
            sizeBy={sizeBy}
          />
          {tooltip}
        </div>
      </div>
    );
  }
}

ClusterGraph.propTypes = {
  styles: PropTypes.object,
  authorNodes: PropTypes.arrayOf(PropTypes.object),
  clusterNodes: PropTypes.arrayOf(PropTypes.object),
  clusterLinks: PropTypes.arrayOf(PropTypes.object),
  filteredAutomationScore: PropTypes.object,
  size: PropTypes.object, // provided by `react-sizeme` HOC
  sizeBy: PropTypes.string,
  selectedViewByOption: PropTypes.string,
  setGraphHover: PropTypes.func,
  selectedTopClustersByVolume: PropTypes.array,
  showSelectionOnly: PropTypes.bool,
  drawNodeToCanvas: PropTypes.func,
  getLinkColor: PropTypes.func,
  tooltip: PropTypes.node,
  hoverMap: PropTypes.object
};

ClusterGraph.defaultProps = {
  authorNodes: [],
  clusterNodes: [],
  clusterLinks: [],
  followerLinks: [],
  selectedTopClustersByVolume: [],
  filteredAutomationScore: {}
};

export default withSize(SIZE_ME_CONFIG)(themeable(ClusterGraph));
